import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import Main from "../layouts/Main";

import CourseDetailsLayout from "../pages/courses/CourseDetailsLayout";
import AdminLayout from "../layouts/AdminLayout";
import ExamLayout from "../layouts/ExamLayout";
import ChatLayout from "../layouts/ChatLayout";
import ClassLayout from "../layouts/ClassLayout";
// Helper to wrap lazy components with Suspense
const withSuspense = (Component, props = {}) => (
  <Suspense fallback={<div>Loading...</div>}>
    <Component {...props} />
  </Suspense>
);

// Lazy imports for all pages/components
// const AdminLayout = lazy(() => import("../layouts/AdminLayout"));
// const ExamLayout = lazy(() => import("../layouts/ExamLayout"));
// const ChatLayout = lazy(() => import("../layouts/ChatLayout"));
// const ClassLayout = lazy(() => import("../layouts/ClassLayout"));
const Home = lazy(() => import("../pages/Home"));
const Courses = lazy(() => import("../pages/courses/Courses"));
const CourseDetails = lazy(() => import("../pages/courses/CourseDetails10ms"));
const CourseDetailsMain = lazy(() => import("../pages/courses/CourseDetailsMain"));
// const CourseDetailsLayout = lazy(() => import("../pages/courses/CourseDetailsLayout"));
const CourseSection = lazy(() => import("../pages/courses/CourseSection"));
const Dashboard = lazy(() => import("../pages/admin/dashboard/Dashboard"));
const CourseList = lazy(() => import("../pages/admin/course/CourseList"));
const EditCourse = lazy(() => import("../pages/admin/course/EditCourse"));
const ExamList = lazy(() => import("../pages/admin/exams/ExamList"));
const Questions = lazy(() => import("../pages/admin/exams/questions/Questions"));
const Login = lazy(() => import("../pages/Login"));
const LoginNew = lazy(() => import("../pages/LoginNew"));
const Register = lazy(() => import("../pages/Register"));
const ChatApp = lazy(() => import("../pages/chat/Chat"));
const Messaging = lazy(() => import("../pages/chat/pages/Messaging"));
const GroupChat = lazy(() => import("../pages/chat/GroupChat"));
const GroupMessaging = lazy(() => import("../pages/chat/pages/GroupMessaging"));
const ExamSettings = lazy(() => import("../pages/admin/exams/ExamSettings"));
const ExamSettingsAccess = lazy(() => import("../pages/admin/exams/examSettings/ExamSettingsAccess"));
const ExamSettingsLeaderboard = lazy(() => import("../pages/admin/exams/examSettings/ExamSettingsLeaderboard"));
const ExamSettingsShare = lazy(() => import("../pages/admin/exams/examSettings/ExamSettingsShare"));
const ExamSheetShare = lazy(() => import("../pages/ExamSheetShare"));
const ExamSheetPrepare = lazy(() => import("../pages/ExamSheetPrepare"));
const ExamSheetAnswerSheet = lazy(() => import("../pages/ExamSheetAnswerSheet"));
const CourseContent = lazy(() => import("../pages/course-contents/CourseContent"));
const SingleContent = lazy(() => import("../pages/course-contents/components/SingleContent"));
const ExamSettingsPublish = lazy(() => import("../pages/admin/exams/examSettings/ExamSettingsPublish"));
const Exam = lazy(() => import("../pages/admin/exams/exam/Exam"));
const ExamResult = lazy(() => import("../pages/admin/exams/exam/components/ExamResult"));
const ClassList = lazy(() => import("../pages/admin/Classes/ClassList"));
const BroadCastPage = lazy(() => import("../pages/admin/Classes/BroadCastPage"));
const Streaming = lazy(() => import("../pages/admin/Classes/Streaming"));
const ViewStream = lazy(() => import("../pages/admin/Classes/ViewStream"));
const Omr = lazy(() => import("../pages/admin/exams/examSettings/omr/Omr"));
const Profile = lazy(() => import("../pages/profile/Profile"));
const ExamResultOmr = lazy(() => import("../pages/admin/exams/exam/components/ExamResultOmr"));
const LiveExamLanding = lazy(() => import("../pages/admin/exams/exam/components/LiveExamLanding"));
const LiveExamResultOmr = lazy(() => import("../pages/admin/exams/exam/components/LiveExamResultOmr"));
const LiveExamResult = lazy(() => import("../pages/admin/exams/exam/components/LiveExamResult"));
const AnswerSheet = lazy(() => import("../pages/answerSheet/AnswerSheet"));
const Performance = lazy(() => import("../pages/performance/Performance"));
const Result = lazy(() => import("../pages/admin/exams/exam/components/Result"));
const EditProfile = lazy(() => import("../pages/admin/editProfile/EditProfile"));
const Pdf = lazy(() => import("../pages/admin/exams/questions/Pdf"));
const CourseDashboard = lazy(() => import("../pages/course_dashboard/CourseDashboard"));
const PromoDashboard = lazy(() => import("../pages/promo_dashboard/PromoDashboard"));
const Checkout = lazy(() => import("../pages/payment/Checkout"));
const CourseEnroll = lazy(() => import("../pages/courses/CourseEnroll"));
const SinglePost = lazy(() => import("../pages/singlePost/SinglePost"));
const PrivateRoute = lazy(() => import("./PrivateRoute"));
const AdminRoute = lazy(() => import("./AdminRoute"));
const ClassesExamSettings = lazy(() => import("../pages/admin/Classes/ClassesExamSettings"));
const ClassesExamSettingsPublish = lazy(() => import("../pages/admin/Classes/ClassesExamSettingsPublish"));

const ClassView = lazy(() => import("../pages/admin/Classes/elements/ClassView"));
const NotFound = lazy(() => import("./NotFound"));
const ClassVideo = lazy(() => import("../pages/courses/ClassVideo"));
const ShowLeaderBoard = lazy(() => import("../pages/profile/ShowLeaderBoard"));
const VideoLink = lazy(() => import("../pages/courses/VideoLink"));
const MyLecture = lazy(() => import("../pages/profile/MyLecture"));
const CourseResult = lazy(() => import("../pages/admin/course/CourseResult"));
const Notification = lazy(() => import("../pages/admin/Notification/Notification"));
const AddResult = lazy(() => import("../pages/admin/AddResult/AddResult"));
const ShowRsultByCsv = lazy(() => import("../pages/admin/AddResult/ShowRsultByCsv"));
const AiChat = lazy(() => import("../pages/chat/pages/ai_chat/AiChat"));
const Routine = lazy(() => import("../pages/Routine/Routine"));
const EditorPage = lazy(() => import("../pages/test/TestEditor"));
const RoutineDemo = lazy(() => import("../pages/Routine/RoutineDemo"));
const SelectImagePage = lazy(() => import("../pages/profile/SelectImagePage"));
const GstPaidCourses = lazy(() => import("../pages/courses/GstPaidCourses"));
const GstFreeCourses = lazy(() => import("../pages/courses/GstFreeCourses"));
const BannerNotification = lazy(() => import("../pages/admin/banner_notification/BannerNotification"));
const AdmissionNews = lazy(() => import("../pages/admin/AdmissionNews/AdmissionNews"));
const News = lazy(() => import("../pages/News/News"));
const Package = lazy(() => import("../pages/package/Package"));
const PaymentSuccess = lazy(() => import("../pages/PaymentSuccess/PaymentSuccess"));
const PaymentCancel = lazy(() => import("../pages/PaymentCancel/PaymentCancel"));
const FromSubmissionList = lazy(() => import("../pages/admin/Form/FromSubmissionList"));
const PreBooking = lazy(() => import("../pages/admin/PreBooking/PreBooking"));
const CQEvaluation = lazy(() => import("../pages/admin/exams/examSettings/CQEvaluation"));
const ChatBox = lazy(() => import("../pages/ChatBox/ChatBox"));
const Support = lazy(() => import("../pages/ChatBox/Support"));
const SmsHistory = lazy(() => import("../pages/admin/SmsHistory/SmsHistory"));
const AddRoutine = lazy(() => import("../pages/admin/AddRoutine/AddRoutine"));
const RoutineItem = lazy(() => import("../pages/admin/AddRoutine/RoutineItem"));
const MentorActivity = lazy(() => import("../pages/admin/MentorActivity/MentorActivity"));
const PracticeExam = lazy(() => import("../pages/PracticeExam/PracticeExam"));

// Context Providers (keep as normal imports)
import { ChatContextProvider } from "../pages/chat/context/ContextProvider";
import { CourseContentContextProvider } from "../pages/course-contents/context/CourseContentContextProvider";
import { ExamFilterContextProvider } from "../pages/admin/exams/examFilter/context/ExamFilterContextProvider";
import { ExamSettingsContextProvider } from "../pages/admin/exams/examSettings/context/ExamSettingsContextProvider";
import ExamContextProvider from "../pages/admin/exams/exam/context/ExamContextProvider";
import AnswerSheetProvider from "../pages/answerSheet/context/AnswerSheetContextProvider";
import PerformanceContextProvider from "../pages/performance/context/PerformanceContextProvider";
import { ExamQuestionsContextProvider } from "../pages/admin/exams/questions/context/ExamQuestionsContextProvider";
import { LoadQuestionsContextProvider } from "../pages/admin/exams/questions/context/LoadQuestionsContextProvider";
import PracticeExam2 from "../pages/PracticeExam/PracticeExam2";
import PracticeExam3 from "../pages/PracticeExam/PracticeExam3";
import PracticeExam4 from "../pages/PracticeExam/PracticeExam4";
import MedilogyApp from "../pages/MedilogyApp/MedilogyApp";
import BookStore from "../pages/BookStore/BookStore";
import BookDetail from "../pages/BookStore/BookDetail";
import Cart from "../pages/BookStore/Cart";
import BookCheckout from "../pages/BookStore/Checkout";
import AddBook from "../pages/admin/AddBook/AddBook";


// Now use withSuspense for all route elements
const router = createBrowserRouter([
  {
    path: "/",
    element: <Main />,
    children: [
      {
        path: "",
        element: withSuspense(Home),
      },
      {
        path: "profile",
        element: withSuspense(PrivateRoute, { children: withSuspense(Profile) }),
      },
      {
        path: "app",
        element: <MedilogyApp />,
      },
      {
        path: "book-store",
        element: <BookStore />,
      },
      {
        path: "book-store/:bookId",
        element: <BookDetail />,
      },
      {
        path: "cart",
        element: <Cart />,
      },
      {
        path: "book-checkout",
        element: <BookCheckout />,
      },
      {
        path: "practice-exam",
        element: withSuspense(PracticeExam),
      },
      {
        path: "practice-exam1",
        element: withSuspense(PracticeExam2),
      },
      {
        path: "practice-exam2",
        element: withSuspense(PracticeExam3),
      },
      {
        path: "practice-exam3",
        element: withSuspense(PracticeExam4),
      },
      {
        path: "profile/select-image",
        element: withSuspense(PrivateRoute, { children: withSuspense(SelectImagePage) }),
      },
      {
        path: "profile/lecture_result/:lectureId",
        element: withSuspense(MyLecture),
      },
      {
        path: "courses",
        element: withSuspense(Courses),
      },
      {
        path: "/courses/paid-courses",
        element: withSuspense(GstPaidCourses),
      },
      {
        path: "/courses/free-courses",
        element: withSuspense(GstFreeCourses),
      },
      {
        path: "checkout",
        element: withSuspense(Checkout),
      },
      {
        path: "post/:slug",
        element: withSuspense(SinglePost),
      },
      {
        path: "payment-success",
        element: withSuspense(PaymentSuccess),
      },
      {
        path: "payment-cancel",
        element: withSuspense(PaymentCancel),
      },
      {
        path: "course/:slug",
        element:  <CourseDetailsLayout />,
        children: [
          {
            path: "",
            element: withSuspense(CourseDetailsMain),
          },
          {
            path: ":section",
            element: withSuspense(CourseSection),
          },
          {
            path: "class/:classId",
            element: (
              <ChatContextProvider>
                {withSuspense(ClassVideo)}
              </ChatContextProvider>
            ),
          },
          {
            path: "class-link/:classId",
            element: withSuspense(VideoLink),
          },
        ],
      },
      {
        path: "course/:slug/enroll",
        element: withSuspense(CourseEnroll),
      },
      {
        path: "/do-exam/:examId/prepare",
        element: withSuspense(ExamSheetPrepare),
      },
      {
        path: "/do-exam/:examId/start",
        element: withSuspense(ExamSheetShare),
      },
      {
        path: "/do-exam/:examId/answer-sheet",
        element: withSuspense(ExamSheetAnswerSheet),
      },
      {
        path: "/package/:packageId",
        element: withSuspense(Package),
      },
    ],
  },
  {
    path: "/test_editor",
    element: withSuspense(EditorPage),
  },
  {
    path: "/pdf",
    element: (
      <ExamQuestionsContextProvider>
        {withSuspense(Pdf)}
      </ExamQuestionsContextProvider>
    ),
  },
  {
    path: "/results_leaderboard/:examRecordId",
    element: withSuspense(ShowLeaderBoard),
  },
  {
    path: "chat_message",
    element: (
      <ChatContextProvider>
        {withSuspense(ChatApp)}
      </ChatContextProvider>
    ),
    children: [
      {
        path: ":id",
        element: withSuspense(Messaging),
      },
      {
        path: "chat_ai",
        element: withSuspense(AiChat),
      },
    ],
  },
  {
    path: "group_chat_message",
    element: withSuspense(GroupChat),
    children: [
      {
        path: ":id",
        element: (
          <ChatContextProvider>
            {withSuspense(GroupMessaging)}
          </ChatContextProvider>
        ),
      },
    ],
  },
  {
    path: "/admin",
    element: (
      <AdminRoute>
        <AdminLayout />
      </AdminRoute>
    ),
    children: [
      {
        path: "",
        element: withSuspense(Dashboard),
      },
      {
        path: "dashboard",
        element: withSuspense(Dashboard),
      },
      {
        path: "notifications",
        element: withSuspense(Notification),
      },
      {
        path: "add-routine",
        element: withSuspense(AddRoutine),
      },
      {
        path: "add-routine/:class_routine",
        element: withSuspense(RoutineItem),
      },
      {
        path: "banner-notifications",
        element: withSuspense(BannerNotification),
      },
      {
        path: "add-results",
        element: withSuspense(AddResult),
      },
      {
        path: "admission-news",
        element: withSuspense(AdmissionNews),
      },
      {
        path: "classes",
        element: withSuspense(ClassList),
      },
      {
        path: "courses",
        element: withSuspense(CourseList),
      },
      {
        path: "form",
        element: withSuspense(FromSubmissionList),
      },
      {
        path: "pre-booking",
        element: withSuspense(PreBooking),
      },
      {
        path: "sms",
        element: withSuspense(SmsHistory),
      },
      {
        path: "mentor-activity",
        element: withSuspense(MentorActivity),
      },
      {
        path: "course/:id",
        element: withSuspense(EditCourse),
      },
      {
        path: "exams",
        element: (
          <ExamFilterContextProvider>
            {withSuspense(ExamList)}
          </ExamFilterContextProvider>
        ),
      },
      {
        path: "edit-profile",
        element: withSuspense(EditProfile),
      },
      {
        path: "myDatabase",
        element: (
          <ExamFilterContextProvider>
            {withSuspense(ExamList)}
          </ExamFilterContextProvider>
        ),
      },
      {
        path: "course-content",
        element: (
          <CourseContentContextProvider>
            {withSuspense(CourseContent)}
          </CourseContentContextProvider>
        ),
      },
      {
        path: "course-content/:id",
        element: (
          <CourseContentContextProvider>
            {withSuspense(SingleContent)}
          </CourseContentContextProvider>
        ),
      },
      {
        path: "broadcast",
        element: withSuspense(BroadCastPage),
      },
      {
        path: ":videoId/view",
        element: withSuspense(ViewStream),
      },
      {
        path: "add-book",
        element: <AddBook />,
      },
    ],
  },
  {
    path: "/admin/class_list/:classId",
    element: <ClassLayout />,
    children: [
      {
        path: "",
        element: withSuspense(ClassesExamSettings),
      },
      {
        path: "settings",
        element: withSuspense(ClassesExamSettings),
      },
      {
        path: "view",
        element: (
          <ChatContextProvider>
            {withSuspense(ClassView)}
          </ChatContextProvider>
        ),
      },
      {
        path: "publish",
        element: withSuspense(ClassesExamSettingsPublish),
      },
      {
        path: "streaming",
        element: withSuspense(ClassesExamSettingsPublish),
      },
    ],
  },
  {
    path: "/admin/exam/:examId",
    element: <ExamLayout />,
    children: [
      {
        path: "",
        element: <Navigate to="questions" replace />,
      },
      {
        path: "questions",
        element: (
          <LoadQuestionsContextProvider>
            {withSuspense(Questions)}
          </LoadQuestionsContextProvider>
        ),
      },
      {
        path: "settings",
        element: (
          <ExamSettingsContextProvider>
            {withSuspense(ExamSettings)}
          </ExamSettingsContextProvider>
        ),
      },
      {
        path: "access",
        element: (
          <ExamSettingsContextProvider>
            {withSuspense(ExamSettingsAccess)}
          </ExamSettingsContextProvider>
        ),
      },
      {
        path: "leaderboard",
        element: withSuspense(ExamSettingsLeaderboard),
      },
      {
        path: "publish",
        element: (
          <ExamSettingsContextProvider>
            {withSuspense(ExamSettingsPublish)}
          </ExamSettingsContextProvider>
        ),
      },
      {
        path: "cq-evaluation",
        element: (
          <ExamSettingsContextProvider>
            {withSuspense(CQEvaluation)}
          </ExamSettingsContextProvider>
        ),
      },
      {
        path: "share",
        element: (
          <ExamSettingsContextProvider>
            {withSuspense(ExamSettingsShare)}
          </ExamSettingsContextProvider>
        ),
      },
      {
        path: "omr",
        element: withSuspense(Omr),
      },
    ],
  },
  {
    path: "answer-sheet",
    element: (
      <AnswerSheetProvider>
        <ExamFilterContextProvider>
          {withSuspense(AnswerSheet)}
        </ExamFilterContextProvider>
      </AnswerSheetProvider>
    ),
  },
  {
    path: "performance",
    element: (
      <PerformanceContextProvider>
        <ExamFilterContextProvider>
          {withSuspense(Performance)}
        </ExamFilterContextProvider>
      </PerformanceContextProvider>
    ),
  },
  {
    path: "/exam/:examId",
    element: (
      <ExamContextProvider>
        {withSuspense(Exam)}
      </ExamContextProvider>
    ),
  },
  {
    path: "/exam_result/:examId/:resultPk",
    element: (
      <ExamContextProvider>
        {withSuspense(ExamResultOmr)}
      </ExamContextProvider>
    ),
  },
  {
    path: "/exam_result/:examId",
    element: (
      <ExamContextProvider>
        {withSuspense(ExamResult)}
      </ExamContextProvider>
    ),
  },
  {
    path: "/exam_result_/:resultPk",
    element: (
      <ExamContextProvider>
        {withSuspense(Result)}
      </ExamContextProvider>
    ),
  },
  {
    path: `/live_exam/:examId`,
    element: (
      <ExamContextProvider>
        {withSuspense(LiveExamLanding)}
      </ExamContextProvider>
    ),
  },
  {
    path: "/live_exam_result/:examId/:resultPk",
    element: (
      <ExamContextProvider>
        {withSuspense(LiveExamResultOmr)}
      </ExamContextProvider>
    ),
  },
  {
    path: "/live_exam_result/:examId",
    element: (
      <ExamContextProvider>
        {withSuspense(LiveExamResult)}
      </ExamContextProvider>
    ),
  },
  {
    path: "/admin/myDatabase/:databaseId",
    element: <ExamLayout />,
    children: [
      {
        path: "",
        element: <Navigate to="questions" replace />,
      },
      {
        path: "questions",
        element: withSuspense(Questions),
      },
      {
        path: "settings",
        element: withSuspense(ExamSettings),
      },
      {
        path: "access",
        element: withSuspense(ExamSettingsAccess),
      },
      {
        path: "leaderboard",
        element: withSuspense(ExamSettingsLeaderboard),
      },
      {
        path: "share",
        element: withSuspense(ExamSettingsShare),
      },
    ],
  },
  {
    path: "/login",
    element: withSuspense(LoginNew),
  },
  {
    path: "/register",
    element: withSuspense(Register),
  },
  {
    path: ":classId/stream",
    element: (
      <ChatContextProvider>
        {withSuspense(Streaming)}
      </ChatContextProvider>
    ),
  },
  {
    path: "/course_dashboard",
    element: withSuspense(CourseDashboard),
  },
  {
    path: "/promo_dashboard",
    element: withSuspense(PromoDashboard),
  },
  {
    path: "/exam-result",
    element: withSuspense(ShowRsultByCsv),
  },
  {
    path: "/course_result/:courseId",
    element: withSuspense(CourseResult),
  },
  {
    path: "*",
    element: withSuspense(NotFound),
  },
  {
    path: "/routine",
    element: withSuspense(Routine),
  },
  {
    path: "/news",
    element: withSuspense(News),
  },
  {
    path: "/routine-demo/:class_routine",
    element: withSuspense(RoutineDemo),
  },
  {
    path: "/chats",
    element: <ChatLayout/>,
    children: [
      {
        path: "ai-chat",
        element: withSuspense(AiChat),
      },
      {
        path: "support-chat",
        element: withSuspense(ChatBox),
      },
    ],
  },
  {
    path: "/admin/support",
    element: withSuspense(Support),
  },
  {
    path: "/course/course-page/agri100D/",
    element: <Navigate to="/course/course-page/75" replace />,
  },
  {
    path: "/course/course-page/43",
    element: <Navigate to="/course/course-page/75" replace />,
  },
]);

export default router;
